import { createSelector } from "@reduxjs/toolkit";
import { selectGameObject } from "features/gamestate/gameStateSlice";
import { selectAllPlayers } from "features/player/playerSlice";
import {
  PUBLISH_STATUS,
  DISPLAY_STATUS_AWAITING,
  getDisplayStatus,
  getRoundKeys,
  neediestRound,
} from "../lifecycle";

/**
 * Round key of the first round whose clue completion is < 100%.
 * Returns null if every round is full. Falls back to "round1" for games
 * with no rounds yet.
 */
export const selectFirstIncompleteRound = createSelector(
  selectGameObject,
  (game) => {
    const keys = getRoundKeys(game);
    if (keys.length === 0) return "round1";
    // neediestRound returns { round, missing }; missing===0 means complete.
    const needy = neediestRound(game);
    if (!needy || needy.missing === 0) return null;
    return needy.round;
  }
);

/**
 * The Hero's state-driven config (spec §5.1). Returns null when no game
 * is loaded so the Hero can render a skeleton. Action handlers (primary /
 * secondary onClick) are NOT included here — the consuming Hero component
 * binds them at render time, since they need navigate / Provider closures.
 *
 * Shape: { state, isAwaitingReview, primary: { label }, secondary?: { label } }
 */
export const selectGameHomeHeroState = createSelector(
  selectGameObject,
  (game) => {
    if (!game) return null;
    const display = getDisplayStatus(game);

    switch (display) {
      case PUBLISH_STATUS.DRAFT:
        return {
          state: "draft",
          isAwaitingReview: false,
          primary: { label: "Keep editing" },
        };
      case DISPLAY_STATUS_AWAITING:
        return {
          state: "awaiting",
          isAwaitingReview: true,
          primary: { label: "Mark Ready" },
          secondary: { label: "Keep editing" },
        };
      case PUBLISH_STATUS.READY:
        return {
          state: "ready",
          isAwaitingReview: false,
          primary: { label: "Launch Game" },
          secondary: { label: "Preview Board" },
        };
      case PUBLISH_STATUS.IN_PROGRESS:
        return {
          state: "in-progress",
          isAwaitingReview: false,
          primary: { label: "Open Control Room" },
          secondary: { label: "Open Board" },
        };
      case PUBLISH_STATUS.PLAYED:
        return {
          state: "played",
          isAwaitingReview: false,
          primary: { label: "View Recap" },
          secondary: { label: "Copy & Replay" },
        };
      case PUBLISH_STATUS.ARCHIVED:
        return {
          state: "archived",
          isAwaitingReview: false,
          primary: { label: "Unarchive" },
        };
      default:
        return {
          state: "draft",
          isAwaitingReview: false,
          primary: { label: "Keep editing" },
        };
    }
  }
);

// PlayerRow's spec contract is { displayName }, but the stored player model
// uses `name`. Normalize at this boundary so PlayerRow stays per-spec and the
// rows don't render blank.
const withDisplayName = (p) => ({ ...p, displayName: p.displayName || p.name });

/**
 * Players summary adapted to lifecycle (spec §9.2). Mode tells the
 * Players card how to render:
 *  - "roster":    pre-game; show join-order list, no ranks
 *  - "standings": in-progress; top-3 with ranks
 *  - "final":     played; full leaderboard with ranks, winner accent
 */
export const selectGamePlayersSummary = createSelector(
  selectGameObject,
  selectAllPlayers,
  (game, players) => {
    const status = getDisplayStatus(game);
    if (status === PUBLISH_STATUS.IN_PROGRESS) {
      const sorted = [...players].sort((a, b) => (b.score || 0) - (a.score || 0));
      return {
        mode: "standings",
        players: sorted.slice(0, 3).map((p, i) => ({ ...withDisplayName(p), rank: i + 1 })),
      };
    }
    if (status === PUBLISH_STATUS.PLAYED) {
      const sorted = [...players].sort((a, b) => (b.score || 0) - (a.score || 0));
      return {
        mode: "final",
        players: sorted.map((p, i) => ({ ...withDisplayName(p), rank: i + 1 })),
      };
    }
    return { mode: "roster", players: players.map(withDisplayName) };
  }
);
